// Filter-row renderers + CSV/clipboard export + SortableHeader.
// All UI that depends on a column's filterType lives here so the column defs
// stay declarative and the page component stays focused on layout/state.

import type { CSSProperties } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { TableHead } from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import type { SalesManager } from '../api';
import type { AccountTableRow } from '../types';
import type { ColumnDef } from './accountsColumnDefs.types';

interface ServerFilters {
  accountNumberSearch?: string;
  nameSearch?: string;
  contractualManagerSearch?: string;
  salesManagerId?: string;
  contractNumberSearch?: string;
}

export function stripAccents(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function getTrimmedFilterValue(
  columnFilters: Record<string, string>,
  key: string,
): string | undefined {
  const value = columnFilters[key]?.trim();
  return value ? value : undefined;
}

function getSalesManagerFilterValue(
  columnFilters: Record<string, string>,
): string | undefined {
  const value = getTrimmedFilterValue(columnFilters, 'sales_manager_name');
  return value && (
    /^[0-9a-f]{24}$/i.test(value)
    || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
  ) ? value : undefined;
}

export function buildServerTextFilters(
  columnFilters: Record<string, string>,
): ServerFilters {
  return {
    accountNumberSearch: getTrimmedFilterValue(columnFilters, 'account_number'),
    nameSearch: getTrimmedFilterValue(columnFilters, 'name'),
    contractualManagerSearch: getTrimmedFilterValue(
      columnFilters,
      'contractual_manager_name',
    ),
    salesManagerId: getSalesManagerFilterValue(columnFilters),
    contractNumberSearch: getTrimmedFilterValue(columnFilters, 'contract_number'),
  };
}

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function exportToCSV(
  items: AccountTableRow[],
  columns: ColumnDef<AccountTableRow>[],
  filename: string,
): void {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function copyToClipboard(
  items: AccountTableRow[],
  columns: ColumnDef<AccountTableRow>[],
): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

export function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      // `h-auto` + `whitespace-normal` lets the label wrap when the column is
      // narrow. `text-left` keeps the wrapped lines flush; `leading-tight`
      // matches the surrounding TableHead. Inherits Button's `text-sm font-medium`
      // so sortable labels match SensorsTab/Invoices/Quotes header typography.
      className="-ml-3 h-auto max-w-full whitespace-normal text-left leading-tight text-muted-foreground hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

/**
 * Format a numeric sum for the 'number-sum' filter cell. Integers get a
 * thousand-separated count (e.g. 12,345); currency gets a $-prefixed value.
 */
function formatSum(total: number, format: 'currency' | 'integer'): string {
  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(total);
  }
  return new Intl.NumberFormat('en-US').format(total);
}

export function renderColumnFilter(
  col: ColumnDef<AccountTableRow>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
  salesManagers: SalesManager[],
  /** Rows currently passing every other filter — used by 'number-sum' columns
   * to display a live running total in the filter cell. */
  filteredRows: AccountTableRow[],
  /** Inline style applied to every TableHead the function emits — used to make
   * the filter row sticky. Inline style beats Tailwind class specificity. */
  thStyle?: CSSProperties,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} style={thStyle} />;

  if (col.filterType === 'number-sum') {
    const total = filteredRows.reduce((sum, row) => {
      const raw = col.getValue(row);
      const n = typeof raw === 'number' ? raw : Number(raw);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    return (
      <TableHead
        key={`filter-${col.key}`}
        className="p-1 text-right tabular-nums text-xs font-semibold text-foreground"
        style={thStyle}
        title={`Sum across ${filteredRows.length} visible row${filteredRows.length === 1 ? '' : 's'}`}
      >
        {formatSum(total, col.sumFormat ?? 'integer')}
      </TableHead>
    );
  }

  if (col.filterType === 'single-select') {
    // Mirror the assignment dropdown on the account-detail page: only currently
    // active account managers appear. Former AMs are hidden — historical accounts
    // can still be opened directly, but the filter chooser is curated to actives.
    const options = col.key === 'sales_manager_name'
      ? salesManagers
          .filter((manager) => !manager.isFormer)
          .map((manager) => ({
            label: manager.name,
            value: manager.id,
          }))
      : (col.filterOptions || []);

    return (
      <TableHead key={`filter-${col.key}`} className="p-1" style={thStyle}>
        <Select
          value={columnFilters[col.key] || '__all__'}
          onValueChange={(value) =>
            setColumnFilter(col.key, value === '__all__' ? '' : value)
          }
        >
          <SelectTrigger
            aria-label={`Filter ${col.label}`}
            className="h-7 w-full min-w-0 text-xs font-normal"
          >
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All</SelectItem>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableHead>
    );
  }

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1" style={thStyle}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs justify-start font-normal"
              aria-label={`Filter ${col.label}`}
            >
              {selected.length ? `${selected.length} selected` : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1" style={thStyle}>
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            aria-label={`${col.label} from`}
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            aria-label={`${col.label} to`}
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1" style={thStyle}>
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            aria-label={`${col.label} minimum`}
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            aria-label={`${col.label} maximum`}
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className="p-1" style={thStyle}>
      <Input
        className="h-7 text-xs"
        aria-label={`Filter ${col.label}`}
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}
